import {
    addParsedDateToDocument,
    DocumentDeleteResults,
    DocumentRead,
    DocumentReadWReferences,
    JobRead,
    DocumentReadWRefs,
    DocumentSearch,
} from "../schemas";
import {ThemeColors} from "../colors";
import {PythonDocumentFilter, TagFilter} from "../schemas/boolean_filtering";
import Swal from "sweetalert2";
import {apiFetch, apiFetchRaw} from "./client";

export async function getDocument(documentId: string): Promise<DocumentReadWReferences> {
    return apiFetch<DocumentReadWReferences>(`/documents/${documentId}`);
}

/**
 * Get a blob URL for loading a PDF. Tries fetching directly from a signed
 * GCS URL first (fast, no backend proxy), falls back to proxied download.
 * Always returns a same-origin blob URL so react-pdf-viewer never hits CORS.
 */
export async function getPdfUrl(documentId: string): Promise<string> {
    // Try signed URL — download bytes directly from GCS
    try {
        const resp = await apiFetch<{ url: string }>(`/documents/pdf_url/${documentId}`);
        if (resp.url) {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 8000);
            const gcsResp = await fetch(resp.url, {signal: controller.signal});
            clearTimeout(timeout);
            if (gcsResp.ok) {
                const blob = await gcsResp.blob();
                return URL.createObjectURL(new Blob([blob], {type: "application/pdf"}));
            }
        }
    } catch (err) {
        console.warn(`[pdf] Signed URL failed for ${documentId}, falling back to proxy:`, err);
    }

    // Fallback: download through backend proxy
    console.log(`[pdf] Using proxy fallback for ${documentId}`);
    const res = await apiFetchRaw(`/documents/read_document/${documentId}`);
    const data = await res.blob();
    return URL.createObjectURL(new Blob([data], {type: "application/pdf"}));
}

/** @deprecated Use getPdfUrl instead */
export async function readDocumentEncodedBytes(documentId: string): Promise<Blob> {
    const res = await apiFetchRaw(`/documents/read_document/${documentId}`);
    const data = await res.blob();
    return new Blob([data], {type: "application/pdf"});
}

export async function searchDocuments(
    docSearch: DocumentSearch,
): Promise<{ docs: DocumentReadWRefs[]; totalCount: number }> {
    interface DocumentSearchQuery {
        keyword?: string;
        offset?: number;
        limit?: number;
        group_id?: string;
        folder_tag_id?: string;
        submitter_id?: string;
        tag_filters?: TagFilter[];
        document_filters?: PythonDocumentFilter[];
        sort_criteria?: { field: string; direction: "asc" | "desc" }[];
    }

    const payload: DocumentSearchQuery = {
        keyword: docSearch.keyword,
        offset: docSearch.offset || 0,
        limit: docSearch.limit || 100,
        group_id: docSearch.group_id,
        folder_tag_id: docSearch.folder_tag_id,
        submitter_id: docSearch.folder_tag_id ? undefined : docSearch.submitter_id,
        tag_filters: docSearch.tag_filters,
        document_filters: docSearch.document_filters || [],
        sort_criteria: docSearch.sort_criteria,
    };

    const resp = await apiFetch<{ docs: DocumentReadWRefs[]; total: number }>(
        "/documents/search",
        {method: "POST", body: payload},
    );
    const docs: DocumentRead[] = resp.docs.map(addParsedDateToDocument);
    return {docs, totalCount: resp.total};
}

export interface FolderPreviewItem {
    folder_id: string;
    files: Array<{ id: string; title?: string; file_name?: string }>;
}

export async function getFolderPreviews(folderIds: string[], limitPerFolder: number = 10): Promise<FolderPreviewItem[]> {
    return apiFetch<FolderPreviewItem[]>("/documents/folder_previews", {
        method: "POST",
        body: { folder_ids: folderIds, limit_per_folder: limitPerFolder },
    });
}

export async function deleteDocuments(ids: string[]): Promise<DocumentDeleteResults> {
    const resp = await apiFetch<DocumentDeleteResults>("/documents/bulk_delete", {
        params: {ids},
    });

    Swal.fire({
        title: "Error",
        icon: "error",
        text: "Unexpected Error occurred",
        confirmButtonColor: ThemeColors.neutralDarkHex,
    });
    return resp;
}

export async function reprocessDocuments(docIds: string[]): Promise<{ job_id: number; doc_count: number; job?: JobRead }> {
    return apiFetch<{ job_id: number; doc_count: number; job?: JobRead }>("/documents/reprocess", {
        method: "POST",
        body: { doc_ids: docIds },
    });
}
